import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';

// ─── Sub-type map ────────────────────────────────────────────────────────────
const SUB_TYPE_MAP = {
  IVF: ['SELF', 'OD', 'DS', 'DET'],
  IUI: ['AIH', 'ATD'],
};
export const getSubTypeOptions = (type) => SUB_TYPE_MAP[type] ?? ['SELF'];
const needsSubTypeSelection = (type) => Boolean(SUB_TYPE_MAP[type]);

// ─── FieldLabel ──────────────────────────────────────────────────────────────
function FieldLabel({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        display: 'block',
        mb: 0.75,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontSize: '0.72rem',
        color: 'text.secondary',
      }}
    >
      {children}
    </Typography>
  );
}

FieldLabel.propTypes = {
  children: PropTypes.node.isRequired,
};

// ─── StyledSelect ─────────────────────────────────────────────────────────────
function StyledSelect({ value, onChange, options, placeholder, primaryColor }) {
  return (
    <Select
      value={value}
      onChange={onChange}
      fullWidth
      displayEmpty
      size="small"
      sx={{
        backgroundColor: 'white',
        fontWeight: 600,
        fontSize: '0.875rem',
        height: '40px',
        mb: 2,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#b2d8e0', borderWidth: '1.5px' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor, borderWidth: '2px' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor, borderWidth: '2px' },
        '& .MuiSelect-icon': { color: primaryColor },
      }}
    >
      <MenuItem value="" disabled>
        <em>{placeholder}</em>
      </MenuItem>
      {options.map((opt) => (
        <MenuItem key={opt} value={opt} sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
          {opt}
        </MenuItem>
      ))}
    </Select>
  );
}

StyledSelect.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  placeholder: PropTypes.string.isRequired,
  primaryColor: PropTypes.string.isRequired,
};

// ─── CreateProcedureDialog ────────────────────────────────────────────────────
export default function CreateProcedureDialog({
  open,
  onClose,
  patient,
  procedureTypes,
  loadingProcedureTypes,
  selectedType,
  onTypeChange,
  selectedSubType,
  onSubTypeChange,
  onSubmit,
  submitting,
  primaryColor,
}) {
  const subTypeOptions = getSubTypeOptions(selectedType);
  const showSubType = Boolean(selectedType);
  const isValid = Boolean(selectedType && selectedSubType);

  const handleTypeChange = (e) => {
    const t = e.target.value;
    onTypeChange(t);
    // auto-set SELF for types that don't need a choice
    if (!needsSubTypeSelection(t)) {
      onSubTypeChange('SELF');
    } else {
      onSubTypeChange('');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.20)',
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, #005f75 100%)`,
          px: 3, pt: 2, pb: 2,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 38, height: 38, borderRadius: '10px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <AddIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '1rem', lineHeight: 1.3 }}>
                Create Procedure
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.72)', mt: 0.2 }} noWrap>
                {patient?.full_name || patient?.name}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.12)' } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ px: 3, py: 2.5 }}>
        {loadingProcedureTypes ? (
          <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} py={3}>
            <CircularProgress size={22} sx={{ color: primaryColor }} />
            <Typography variant="body2" color="text.secondary">Loading procedure types...</Typography>
          </Box>
        ) : (
          <Box>
            {/* Type */}
            <FieldLabel>Procedure Type</FieldLabel>
            <StyledSelect
              value={selectedType}
              onChange={handleTypeChange}
              options={procedureTypes}
              placeholder="Select procedure type..."
              primaryColor={primaryColor}
            />

            {/* Sub-Type (conditional) */}
            {showSubType && (
              <>
                <FieldLabel>Sub-Type</FieldLabel>
                <StyledSelect
                  value={selectedSubType}
                  onChange={(e) => onSubTypeChange(e.target.value)}
                  options={subTypeOptions}
                  placeholder="Select sub-type..."
                  primaryColor={primaryColor}
                />
              </>
            )}

            {/* Actions */}
            <Box display="flex" justifyContent="flex-end" gap={1} mt={0.5}>
              <Button
                variant="outlined"
                size="small"
                onClick={onClose}
                sx={{
                  borderColor: '#dee2e6', color: '#6c757d',
                  textTransform: 'none', fontWeight: 600,
                  borderRadius: '7px', px: 2.5,
                  '&:hover': { borderColor: '#adb5bd', backgroundColor: 'rgba(0,0,0,0.03)' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={onSubmit}
                variant="contained"
                size="small"
                disabled={submitting || !isValid}
                sx={{
                  backgroundColor: `${primaryColor} !important`,
                  color: 'white !important',
                  textTransform: 'none', fontWeight: 700,
                  borderRadius: '7px', px: 2.5,
                  '&:hover': { backgroundColor: '#005f75 !important' },
                  '&.Mui-disabled': { opacity: 0.55 },
                }}
              >
                {submitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Create Procedure'}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

CreateProcedureDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patient: PropTypes.shape({
    full_name: PropTypes.string,
    name: PropTypes.string,
  }),
  procedureTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  loadingProcedureTypes: PropTypes.bool.isRequired,
  selectedType: PropTypes.string.isRequired,
  onTypeChange: PropTypes.func.isRequired,
  selectedSubType: PropTypes.string.isRequired,
  onSubTypeChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  primaryColor: PropTypes.string.isRequired,
};

CreateProcedureDialog.defaultProps = {
  patient: null,
};
